// src/components/About.js
import React, { useState, useEffect, useRef } from "react";
import {
  FaChalkboardTeacher, FaBook, FaBus, FaFutbol, FaFlask, FaLaptop,
  FaBullseye, FaEye, FaTrophy, FaMedal, FaCertificate, FaUserGraduate,
  FaUsers, FaShieldAlt, FaPlus, FaMinus, FaQuoteLeft,
} from "react-icons/fa";
import {
  motion, MotionConfig, useReducedMotion, useScroll, useTransform,
} from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, A11y } from "swiper/modules";
import CountUp from "react-countup";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { optimizeUrl } from "../utils/uploadImage";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./About.css";

/* ------------------------------------------------------------------ */
/* Static data                                                          */
/* ------------------------------------------------------------------ */
const STATS = [
  { icon: FaUsers, end: 1500, label: "Students" },
  { icon: FaChalkboardTeacher, end: 80, label: "Teachers" },
  { icon: FaUserGraduate, end: 5000, label: "Alumni" },
  { icon: FaTrophy, end: 120, label: "Awards" },
];

const WHY = [
  { icon: FaChalkboardTeacher, title: "Expert Faculty", text: "Highly qualified teachers dedicated to every student's growth and success." },
  { icon: FaLaptop, title: "Modern Learning", text: "Smart classrooms, digital resources and innovative teaching methods." },
  { icon: FaFutbol, title: "Beyond Academics", text: "Strong focus on sports, arts and extracurricular excellence." },
  { icon: FaShieldAlt, title: "Safe & Supportive", text: "A nurturing and secure environment for the whole child." },
];

const FACILITIES = [
  { icon: FaBook, title: "Library" },
  { icon: FaFlask, title: "Science Labs" },
  { icon: FaLaptop, title: "Computer Labs" },
  { icon: FaFutbol, title: "Sports" },
  { icon: FaBus, title: "Transport" },
];

const ACHIEVEMENTS = [
  { icon: FaTrophy, title: "Best School Award", text: "Recognized as a top institution for academic excellence." },
  { icon: FaMedal, title: "Sports Excellence", text: "Students consistently win district and national competitions." },
  { icon: FaCertificate, title: "Academic Excellence", text: "Top results and scholarship holders in board and international exams." },
  { icon: FaUserGraduate, title: "Successful Alumni", text: "Thousands of graduates excelling in careers worldwide." },
];

const FAQS = [
  { q: "What are the admission requirements?", a: "Admission is based on an entrance test, previous academic records, and an interview with the student and parents." },
  { q: "Do you offer transportation facilities?", a: "Yes, we provide safe and reliable school bus services covering major areas of the city." },
  { q: "What extracurricular activities are available?", a: "We offer sports, music, art, science clubs, and debate competitions." },
  { q: "How do you ensure student safety?", a: "We have CCTV monitoring, trained security staff, and strict protocols to ensure the safety of every child." },
  { q: "What is the fee structure?", a: "Our fees are designed to be affordable while maintaining high educational standards. Please contact the admission office for details." },
];

/* ------------------------------------------------------------------ */
/* Animation helpers                                                    */
/* ------------------------------------------------------------------ */
const EASE = [0.22, 1, 0.36, 1];
const VIEW = { once: true, amount: 0.15 };

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};
const pop = {
  hidden: { opacity: 0, scale: 0.92 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
};
const stagger = (gap = 0.1) => ({
  hidden: {},
  show: { transition: { staggerChildren: gap } },
});

const Heading = ({ title, sub }) => (
  <motion.header className="ab-head" variants={fade} initial="hidden" whileInView="show" viewport={VIEW}>
    <h2>{title}</h2>
    {sub && <p>{sub}</p>}
  </motion.header>
);

const Grid = ({ cols, children, className = "" }) => (
  <motion.div
    className={`ab-grid ab-grid-${cols} ${className}`}
    variants={stagger(0.09)}
    initial="hidden"
    whileInView="show"
    viewport={VIEW}
  >
    {children}
  </motion.div>
);
const Cell = ({ children, variants = fade }) => (
  <motion.div className="ab-cell" variants={variants}>{children}</motion.div>
);

/* Avatar with initials fallback */
function Avatar({ src, name, className, size }) {
  const [bad, setBad] = useState(false);
  const initials = name
    ? name.replace(/^(Mr|Ms|Mrs|Dr|H)\s+/i, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";

  if (bad || !src) {
    return (
      <div className={`${className} ab-fallback`} role="img" aria-label={name}>{initials}</div>
    );
  }
  return (
    <img
      className={className}
      src={src}
      alt={name}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setBad(true)}
    />
  );
}

const onTimeLeft = (swiper, _time, progress) => {
  swiper.el?.style.setProperty("--ab-progress", String(1 - progress));
};

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function About() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [heroUrl, setHeroUrl]         = useState("");
  const [admBanner, setAdmBanner]     = useState({ imageUrl: "", heading: "" });

  // ── NEW: dynamic from Firestore ──
  const [teamMembers, setTeamMembers] = useState([]);
  const [alumniList, setAlumniList]   = useState([]);

  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  useEffect(() => {
    const unsubs = [
      onSnapshot(
        doc(db, "settings", "aboutHero"),
        (s) => setHeroUrl(s.exists() ? s.data().imageUrl || "" : ""),
        (e) => console.error("About hero:", e.message)
      ),
      onSnapshot(
        doc(db, "settings", "admissionBanner"),
        (s) => setAdmBanner(s.exists()
          ? { imageUrl: s.data().imageUrl || "", heading: s.data().heading || "" }
          : { imageUrl: "", heading: "" }),
        (e) => console.error("Adm banner:", e.message)
      ),
      // NEW: Team from Firestore (sorted by order)
      onSnapshot(collection(db, "team"), (s) => {
        const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setTeamMembers(list);
      }, (e) => console.error("Team:", e.message)),
      // NEW: Alumni from Firestore (sorted by order)
      onSnapshot(collection(db, "alumni"), (s) => {
        const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setAlumniList(list);
      }, (e) => console.error("Alumni:", e.message)),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  const swiperCommon = {
    modules: [Navigation, Pagination, Autoplay, A11y],
    spaceBetween: 24,
    navigation: true,
    pagination: { clickable: true },
    rewind: true,
    grabCursor: true,
    watchOverflow: true,
    onAutoplayTimeLeft: onTimeLeft,
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="about-page">

        {/* ============ Hero ============ */}
        <section className="ab-hero" ref={heroRef}>
          {heroUrl && (
            <motion.img
              className="ab-hero-img"
              src={optimizeUrl(heroUrl, 1280)}
              srcSet={`${optimizeUrl(heroUrl, 768)} 768w, ${optimizeUrl(heroUrl, 1280)} 1280w, ${optimizeUrl(heroUrl, 1920)} 1920w`}
              sizes="100vw"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              decoding="async"
              style={reduce ? undefined : { y: heroY }}
            />
          )}
          <div className="ab-hero-overlay" />
          <motion.div className="ab-container ab-hero-inner" variants={stagger(0.15)} initial="hidden" animate="show">
            <motion.h1 variants={fade}>Where Knowledge <br /> Meets Character</motion.h1>
            <motion.p variants={fade}>A place where every student discovers their true potential through strong academics, character and care.</motion.p>
            <motion.div className="ab-hero-actions" variants={fade}>
              <Link to="/admissions" className="ab-btn ab-btn-gold">Apply for Admission</Link>
              <a href="#ab-why" className="ab-btn ab-btn-ghost">Why choose us</a>
            </motion.div>
          </motion.div>
        </section>

        {/* ============ Stats ============ */}
        <motion.section
          className="ab-container ab-stats"
          aria-label="School at a glance"
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          {STATS.map(({ icon: Icon, end, label }) => (
            <motion.div key={label} className="ab-stat" variants={pop}>
              <Icon aria-hidden="true" />
              <strong>
                <CountUp end={end} duration={reduce ? 0 : 2.5} separator="," suffix="+" enableScrollSpy scrollSpyOnce />
              </strong>
              <span>{label}</span>
            </motion.div>
          ))}
        </motion.section>

        {/* ============ Who we are ============ */}
        <section className="ab-section">
          <div className="ab-container">
            <Heading title="Who We Are" sub="Our school is committed to holistic education, combining academics, sports and extracurricular activities to prepare students for a brighter future." />
            <Grid cols={2}>
              <Cell>
                <article className="ab-panel">
                  <FaBullseye aria-hidden="true" />
                  <h3>Our Mission</h3>
                  <p>To empower students with knowledge and skills to excel academically and socially.</p>
                </article>
              </Cell>
              <Cell>
                <article className="ab-panel">
                  <FaEye aria-hidden="true" />
                  <h3>Our Vision</h3>
                  <p>To create responsible global citizens with strong values and a lifelong passion for learning.</p>
                </article>
              </Cell>
            </Grid>
          </div>
        </section>

        {/* ============ Director ============ */}
        <section className="ab-section ab-alt">
          <div className="ab-container">
            <Heading title="Director's Message" />
            <div className="ab-director">
              <motion.div
                className="ab-director-photo"
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={VIEW}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <Avatar src="/images/Director.webp" name="Mr Nadeem Shahzad" size={280} />
              </motion.div>
              <motion.blockquote
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={VIEW}
                transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
              >
                <FaQuoteLeft className="ab-quote-mark" aria-hidden="true" />
                <p>Welcome to our school, a place where we nurture creativity, knowledge and excellence. Our goal is to inspire students to achieve their full potential and contribute positively to society. We believe in an inclusive, safe and supportive environment for all.</p>
                <footer>
                  <strong>Mr Nadeem Shahzad</strong>
                  <span>Director</span>
                </footer>
              </motion.blockquote>
            </div>
          </div>
        </section>

        {/* ============ Why choose us ============ */}
        <section className="ab-section" id="ab-why">
          <div className="ab-container">
            <Heading title="Why Choose Our School?" />
            <Grid cols={4}>
              {WHY.map(({ icon: Icon, title, text }) => (
                <Cell key={title}>
                  <article className="ab-card">
                    <span className="ab-icon"><Icon aria-hidden="true" /></span>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ============ Facilities ============ */}
        <section className="ab-section ab-navy">
          <div className="ab-container">
            <Heading title="Our Facilities" sub="Spaces designed for learning, discovery and play." />
            <Grid cols={5}>
              {FACILITIES.map(({ icon: Icon, title }) => (
                <Cell key={title} variants={pop}>
                  <div className="ab-facility">
                    <Icon aria-hidden="true" />
                    <h3>{title}</h3>
                  </div>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ============ Achievements ============ */}
        <section className="ab-section">
          <div className="ab-container">
            <Heading title="Our Achievements" />
            <Grid cols={4}>
              {ACHIEVEMENTS.map(({ icon: Icon, title, text }) => (
                <Cell key={title}>
                  <article className="ab-card ab-card-gold">
                    <span className="ab-icon"><Icon aria-hidden="true" /></span>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </article>
                </Cell>
              ))}
            </Grid>
          </div>
        </section>

        {/* ============ Admission Banner (admin controlled) ============ */}
        {admBanner.imageUrl && (
          <section className="ab-section ab-alt" aria-label="Admissions Open">
            <div className="ab-container">
              <motion.div
                className="ab-banner"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <h2>{admBanner.heading || "Admissions Open – Enroll Now"}</h2>
                <img src={optimizeUrl(admBanner.imageUrl, 1400)} alt="Admissions open banner" loading="lazy" decoding="async" />
              </motion.div>
            </div>
          </section>
        )}

        {/* ============ Team (Firestore — dynamic) ============ */}
        {teamMembers.length > 0 && (
          <section className="ab-section ab-dotted">
            <div className="ab-container">
              <Heading title="Meet Our Team" sub="The people who lead and care for our school every day." />
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEW}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <Swiper
                  {...swiperCommon}
                  className="ab-swiper ab-team-swiper"
                  slidesPerView={1}
                  autoplay={{ delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true }}
                  breakpoints={{ 520: { slidesPerView: 2 }, 900: { slidesPerView: 3 } }}
                >
                  {teamMembers.map((t) => (
                    <SwiperSlide key={t.id}>
                      <article className="ab-team">
                        <div className="ab-team-ring">
                          <Avatar
                            src={t.imageUrl ? optimizeUrl(t.imageUrl, 300) : ""}
                            name={t.name || ""}
                            size={150}
                          />
                        </div>
                        <h3>{t.name}</h3>
                        <p>{t.role}</p>
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </motion.div>
            </div>
          </section>
        )}

        {/* ============ Alumni (Firestore — dynamic) ============ */}
        {alumniList.length > 0 && (
          <section className="ab-section ab-alt">
            <div className="ab-container">
              <Heading title="Our Proud Alumni" />
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEW}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <Swiper
                  {...swiperCommon}
                  className="ab-swiper"
                  slidesPerView={1}
                  autoplay={{ delay: 4500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                  breakpoints={{ 768: { slidesPerView: 2 }, 1100: { slidesPerView: 3 } }}
                >
                  {alumniList.map((a) => (
                    <SwiperSlide key={a.id}>
                      <figure className="ab-alumni">
                        <Avatar
                          src={a.imageUrl ? optimizeUrl(a.imageUrl, 200) : ""}
                          name={a.name || ""}
                          size={84}
                        />
                        <blockquote>"{a.feedback}"</blockquote>
                        <figcaption>
                          <strong>{a.name}</strong>
                          <span>{a.role}</span>
                        </figcaption>
                      </figure>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </motion.div>
            </div>
          </section>
        )}

        {/* ============ FAQ ============ */}
        <section className="ab-section">
          <div className="ab-container ab-narrow">
            <Heading title="Frequently Asked Questions" />
            <motion.div className="ab-faq" variants={stagger(0.08)} initial="hidden" whileInView="show" viewport={VIEW}>
              {FAQS.map((f, i) => {
                const open = activeIndex === i;
                return (
                  <motion.div key={f.q} variants={fade} className={`ab-faq-item ${open ? "is-open" : ""}`}>
                    <button
                      type="button"
                      id={`faq-btn-${i}`}
                      aria-expanded={open}
                      aria-controls={`faq-${i}`}
                      onClick={() => setActiveIndex(open ? null : i)}
                    >
                      <span>{f.q}</span>
                      {open ? <FaMinus aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
                    </button>
                    <div id={`faq-${i}`} className="ab-faq-body" role="region" aria-labelledby={`faq-btn-${i}`}>
                      <div><p>{f.a}</p></div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* ============ CTA ============ */}
        <section className="ab-cta">
          <motion.div className="ab-container" variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={VIEW}>
            <motion.h2 variants={fade}>Join our school today</motion.h2>
            <motion.p variants={fade}>A hub of academic excellence, nurturing young minds with innovation, values and a lifelong love of learning.</motion.p>
            <motion.div variants={fade}>
              <Link to="/admissions" className="ab-btn ab-btn-gold">Apply for Admission</Link>
            </motion.div>
          </motion.div>
        </section>

      </div>
    </MotionConfig>
  );
}